import { FileStack } from "lucide-react";
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from "@tanstack/react-router";

import { AuthenticatedRoute, PublicRoute, RouteLoading } from "./components/auth-route";
import { PlaceholderPage } from "./pages/placeholder-page";

const AccountAccessRoute = lazyRouteComponent(
  () => import("./components/access-routes"),
  "AccountAccessRoute",
);
const ConnectionsAccessRoute = lazyRouteComponent(
  () => import("./components/access-routes"),
  "ConnectionsAccessRoute",
);
const CreateOrganizationAccessRoute = lazyRouteComponent(
  () => import("./components/access-routes"),
  "CreateOrganizationAccessRoute",
);
const InvestigatorAccessRoute = lazyRouteComponent(
  () => import("./components/access-routes"),
  "InvestigatorAccessRoute",
);
const OnboardingAccessRoute = lazyRouteComponent(
  () => import("./components/access-routes"),
  "OnboardingAccessRoute",
);
const SettingsAccessRoute = lazyRouteComponent(
  () => import("./components/access-routes"),
  "SettingsAccessRoute",
);
const WorkspaceAccessRoute = lazyRouteComponent(
  () => import("./components/access-routes"),
  "WorkspaceAccessRoute",
);
const LoginPage = lazyRouteComponent(() => import("./pages/auth-page"), "LoginPage");
const SignupPage = lazyRouteComponent(() => import("./pages/auth-page"), "SignupPage");
const DocumentsPage = lazyRouteComponent(
  () => import("./pages/documents-page"),
  "DocumentsPage",
);
const ExportsPage = lazyRouteComponent(
  () => import("./pages/exports-page"),
  "ExportsPage",
);
const FoundationOverview = lazyRouteComponent(
  () => import("./pages/foundation-overview"),
  "FoundationOverview",
);
const ImprintPage = lazyRouteComponent(
  () => import("./pages/imprint-page"),
  "ImprintPage",
);
const InvitationPage = lazyRouteComponent(
  () => import("./pages/invitation-page"),
  "InvitationPage",
);
const OAuthSignupPage = lazyRouteComponent(
  () => import("./pages/oauth-signup-page"),
  "OAuthSignupPage",
);
const PasswordResetPage = lazyRouteComponent(
  () => import("./pages/password-reset-page"),
  "PasswordResetPage",
);
const PrivacyPage = lazyRouteComponent(
  () => import("./pages/privacy-page"),
  "PrivacyPage",
);
const ProductsPage = lazyRouteComponent(
  () => import("./pages/products-page"),
  "ProductsPage",
);
const TransactionsPage = lazyRouteComponent(
  () => import("./pages/transactions-page"),
  "TransactionsPage",
);

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => (
    <PlaceholderPage
      description="The requested workspace route does not exist."
      icon={FileStack}
      title="Page not found"
    />
  ),
});

const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  component: PublicRoute,
});

const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/signup",
  component: SignupPage,
});

const oauthSignupRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/signup/complete",
  component: OAuthSignupPage,
});

const passwordResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: PasswordResetPage,
});

const imprintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/imprint",
  component: ImprintPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  component: InvitationPage,
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: AuthenticatedRoute,
});

const workspaceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: "workspace",
  component: WorkspaceAccessRoute,
});

const indexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  component: FoundationOverview,
});

const documentsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/documents",
  component: DocumentsPage,
});

const exportsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/exports",
  component: ExportsPage,
});

const productsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/products",
  component: ProductsPage,
});

const transactionsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/transactions",
  component: TransactionsPage,
});

const connectionsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/connections",
  component: ConnectionsAccessRoute,
});

const investigatorRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/investigator",
  component: InvestigatorAccessRoute,
});

const settingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings",
  component: SettingsAccessRoute,
});

const accountRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/account",
  component: AccountAccessRoute,
});

const createOrganizationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/organizations/new",
  component: CreateOrganizationAccessRoute,
});

const onboardingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/onboarding",
  component: OnboardingAccessRoute,
});

const routeTree = rootRoute.addChildren([
  imprintRoute,
  invitationRoute,
  passwordResetRoute,
  privacyRoute,
  publicRoute.addChildren([loginRoute, signupRoute, oauthSignupRoute]),
  authenticatedRoute.addChildren([
    accountRoute,
    createOrganizationRoute,
    onboardingRoute,
    workspaceRoute.addChildren([
      indexRoute,
      documentsRoute,
      productsRoute,
      exportsRoute,
      transactionsRoute,
      connectionsRoute,
      investigatorRoute,
      settingsRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPendingComponent: RouteLoading,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
